// @flow
import clientLogger from './clientLogger';

/**
 * setJSONItem()
 * Updates localStorage entry in given namespace with provided data value
 * Performs: WRITE
 * @param {string} itemKey - namespace of local storage entry
 * @param {Object} item - Object containing data to be stringified into localStorage
 */

export function setJSONItem({
    itemKey,
    item
}: {
    itemKey: string,
    item: Object
}): boolean {
    try {
        localStorage.setItem(itemKey, JSON.stringify(item));
        return true;
    } catch (e) {
        clientLogger.captureException(`${itemKey} write handled exception:`, e); //eslint-disable-line
        return false;
    }
}

/**
 * getJSONItem()
 * Retrieves and parses a stringified value from localStorage
 * Performs: READ/CONDITIONAL WRITE
 * @param {string} itemKey - namespace of local storage item
 * @param {number} expiredBy
 */

export function getJSONItem({
    itemKey,
    itemAttributeKey,
    expiredBy
}: {
    itemKey: string,
    itemAttributeKey?: string,
    expiredBy?: number
}): Object | boolean {
    let item = false;
    const now = parseInt(Date.now() / 1000, 10);
    try {
        item = JSON.parse(localStorage.getItem(itemKey) || '{}');
    } catch (e) {
        clientLogger.captureException(
            `${itemKey} access handled exception:`,
            e
        ); //eslint-disable-line
        return false;
    }

    if (!itemAttributeKey) {
        return item; // Return just the localStorage[itemKey]
    }

    if (!item[itemAttributeKey]) {
        return false; // No key was found in localStorage[itemKey][itemAttributeKey]
    }

    // Process expiredBy, if expired, remove the record and return false
    if (
        typeof expiredBy === 'number' &&
        item[itemAttributeKey].created < now - expiredBy
    ) {
        delete item[itemAttributeKey];
        setJSONItem({
            itemKey,
            item
        });
        return false;
    }
    return item[itemAttributeKey];
}

/**
 * updateJSONItem()
 * Update localStorage item that is encoded in JSON given itemKey, itemAttributeKey, and itemAttributeValue
 * Performs: READ/WRITE
 * @param {string} itemKey - namespace of localStorage item
 * @param {string} itemAttributeKey - localStorage item attribute key
 * @param {any} itemAttributeValue - new itemAttributeValue
 */
export function updateJSONItem({
    itemKey,
    itemAttributeKey,
    itemAttributeValue
}: {
    itemKey: string,
    itemAttributeKey: string,
    itemAttributeValue: Object
}): boolean {
    // $FlowFixMe
    const item: Object = getJSONItem({itemKey}) || {};
    const now = parseInt(Date.now() / 1000, 10);

    item[itemAttributeKey] = {
        ...itemAttributeValue,
        created: now
    };

    return setJSONItem({
        itemKey,
        item
    });
}

/**
 * pruneItem()
 * Cleans up localStorage given an itemKey and several options
 * @param {string} itemKey
 * @param {number} expiredBy - seconds
 * @param {number} maxItems
 */
export function pruneItem({
    itemKey,
    expiredBy = 86400,
    maxItems = 150
}: {
    itemKey: string,
    expiredBy?: number,
    maxItems?: number
}): number {
    // $FlowFixMe
    const item: Object = getJSONItem({itemKey}) || {};
    const now = parseInt(Date.now() / 1000, 10);
    const expired = now - expiredBy;
    let itemKeys = Object.keys(item);
    let itemsPruned = 0;

    // remove expired entries first.
    itemKeys.forEach((key) => {
        if (item[key].created < expired) {
            delete item[key];
            itemsPruned++;
        }
    });

    // if still exceeds max, sort and cut off oldest to maximum
    itemKeys = Object.keys(item);
    if (itemKeys.length > maxItems) {
        const lengthToPrune = itemKeys.length - maxItems;
        // perform sort
        itemKeys = itemKeys.sort((a, b) => {
            if (item[a].created < item[b].created) {
                return -1;
            }
            if (item[a].created > item[b].created) {
                return 1;
            }
            return 0;
        });

        for (let i = 0; i < lengthToPrune; i++) {
            delete item[itemKeys[i]];
        }
        itemsPruned += lengthToPrune;
    }

    if (itemsPruned > 0) {
        setJSONItem({
            itemKey,
            item
        });
    }

    return itemsPruned;
}
